import { useEffect, useState } from 'react';
import GroupsIcon from '@mui/icons-material/Groups';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PaymentsIcon from '@mui/icons-material/Payments';
import WorkIcon from '@mui/icons-material/Work';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SchoolIcon from '@mui/icons-material/School';
import PercentIcon from '@mui/icons-material/Percent';
import { DashboardLayout } from '@/shared/layouts/DashboardLayout';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { QuickActionGrid } from '@/components/ui/QuickActionGrid';
import { PageSection } from '@/components/ui/PageSection';
import { hrApi } from '@/api';
import type { HrDashboardSummary } from '@/types/hr';
import { showApiError } from '@/utils/apiError';

const QUICK_ACTIONS = [
  { to: '/hr/employees', label: 'Employee 360', description: 'Profiles, documents, and history' },
  { to: '/hr/ess', label: 'ESS Portal', description: 'Self-service for employees' },
  { to: '/hr/payroll', label: 'Payroll', description: 'Runs, payslips, and statutory' },
  { to: '/hr/recruitment', label: 'Recruitment', description: 'Open positions and candidates' },
  { to: '/hr/setup', label: 'Setup', description: 'Departments, grades, and policies' },
];

export function HrDashboardPage() {
  const [stats, setStats] = useState<HrDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrApi
      .dashboardSummary()
      .then(setStats)
      .catch((err) => showApiError(err, 'Failed to load HR dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout
        moduleLabel="HR"
        title="HR Dashboard"
        description="Workforce, time, pay, and talent at a glance"
      >
        <DashboardSkeleton kpiCount={8} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      moduleLabel="HR"
      title="HR Dashboard"
      description="Workforce, time, pay, and talent at a glance"
    >
      <KpiGrid>
        <StatCard title="Headcount" value={stats?.headcount ?? '—'} icon={<GroupsIcon />} />
        <StatCard title="Attendance %" value={stats?.attendancePercent ?? '—'} icon={<FactCheckIcon />} />
        <StatCard title="Pending leaves" value={stats?.pendingLeaves ?? '—'} icon={<EventNoteIcon />} />
        <StatCard title="Payroll runs" value={stats?.payrollRuns ?? '—'} icon={<PaymentsIcon />} />
        <StatCard title="Open positions" value={stats?.openPositions ?? '—'} icon={<WorkIcon />} />
        <StatCard title="Candidates" value={stats?.candidatesInPipeline ?? '—'} icon={<PersonSearchIcon />} />
        <StatCard title="Training" value={stats?.trainingAssignments ?? '—'} icon={<SchoolIcon />} />
        <StatCard title="Leave %" value={stats?.leavePercent ?? '—'} icon={<PercentIcon />} />
      </KpiGrid>

      <PageSection title="Quick actions">
        <QuickActionGrid items={QUICK_ACTIONS} columns={3} />
      </PageSection>
    </DashboardLayout>
  );
}
